import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LabelListItem from "../../components/LabelListItem";
import { useParams } from "react-router";
import { useBaustelle } from "../../client/queries";
import { Field, Formik } from "formik";
import { useEffect, useState } from "react";
import { TextField } from "formik-mui";
import {
  useGetUmsatzMengenAuswertungByBaustellenId,
  useGetUmsatzMengenAuswertungByDate,
  useGetUmsatzStundenAuswertungByDate,
  useSucheUmsatzAuswertungByBaustellenId,
  useSucheUmsatzAuswertungByBaustellenIdAndByDate,
} from "../../client/hooks";
import {
  UmsatzAuswertungDaten,
  UmsatzAuswertungListe,
  UmsatzAuswertungRequest,
  UmsatzmengenAuswertung,
} from "../../api";
import { useT } from "../../i18n";
import * as yup from "yup";
import { useMutation, useQueryClient } from "react-query";
import ErrorAlert from "../../components/ErrorAlert";
import { notPermitted } from "../../errors";
import Guard from "../../components/Guard";
import { formatGermanNumber } from "../../formatterHelpers";
import BaustellenMaterialCard from "../../components/baustellen/BaustellenMaterialCard";

export default function DetailsBaustellenKonto() {
  const { id } = useParams<{ id: string }>();
  const baustelle = useBaustelle(id ?? "");
  const fetchUmsatzAuswertung = useSucheUmsatzAuswertungByBaustellenId();
  const fetchMenge = useGetUmsatzMengenAuswertungByBaustellenId();
  const { mutateAsync: vonMutate } = useKontoAuswertungByDate(id ?? "", "VON");
  const { mutateAsync: anMutate } = useKontoAuswertungByDate(id ?? "", "AN");
  const { mutateAsync: personalMutate } = useKontoStundenAuswertungByDate(
    id ?? "",
    "PS"
  );
  const { mutateAsync: fuhrparkMutate } = useKontoStundenAuswertungByDate(
    id ?? "",
    "FP"
  );
  const { mutateAsync } = useUmsatzAuwertung(id ?? "");
  const [data, setData] = useState<UmsatzAuswertungListe>();
  const [kontoVon, setKontoVon] = useState<UmsatzmengenAuswertung>();
  const [kontoAn, setKontoAn] = useState<UmsatzmengenAuswertung>();
  const [personalStunden, setPersonalStunden] = useState<number>(0);
  const [fuhrparkStunden, setFuhrparkStunden] = useState<number>(0);
  const [loading, setLoading] = useState<Boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [startDatum, setStartDatum] = useState("");
  const [endDatum, setEndDatum] = useState(
    new Date().toISOString().split("T")[0]
  );
  useEffect(() => {
    // Define the ID you want to search for
    const baustellenId = id ?? "";

    // Fetch data using the custom hook
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await fetchUmsatzAuswertung(baustellenId);
        setData(result);
        const kontoVon = await fetchMenge(baustellenId, "VON");
        setKontoVon(kontoVon);
        const kontoAn = await fetchMenge(baustellenId, "AN");
        setKontoAn(kontoAn);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, fetchUmsatzAuswertung]);
  useEffect(() => {
    const fetchLimitedData = async () => {
      const result = await mutateAsync({
        startDate: startDatum,
        endDate: endDatum,
      });
      setData(result);
      const kontoVon = await vonMutate({
        startDate: startDatum,
        endDate: endDatum,
      });
      if (kontoVon !== undefined) {
        setKontoVon(kontoVon);
      }
      const kontoAn = await anMutate({
        startDate: startDatum,
        endDate: endDatum,
      });
      if (kontoAn !== undefined) {
        setKontoAn(kontoAn);
      }
      const personalStunden = await personalMutate({
        startDate: startDatum,
        endDate: endDatum,
      });
      if (personalStunden !== undefined) {
        setPersonalStunden(personalStunden.summeArbeitszeit ?? 0);
      }
      const fuhrparkStunden = await fuhrparkMutate({
        startDate: startDatum,
        endDate: endDatum,
      });
      if (fuhrparkStunden !== undefined) {
        setFuhrparkStunden(fuhrparkStunden.summeArbeitszeit ?? 0);
      }
    };
    fetchLimitedData();
  }, [startDatum, endDatum]);
  let schema = yup.object().shape({
    startDate: yup.string(),
    endDate: yup.string(),
  });
  const sumKosten = data
    ? data.items.reduce(
        (sum, row) => sum + parseFloat(`${row.kosten ?? "0"}`),
        0
      )
    : 0;
  const sumErtrag = data
    ? data.items.reduce(
        (sum, row) => sum + parseFloat(`${row.ertrag ?? "0"}`),
        0
      )
    : 0;
  const sumSaldo = data
    ? data.items.reduce(
        (sum, row) => sum + parseFloat(`${row.saldo ?? "0"}`),
        0
      )
    : 0;
  const { t } = useT("konto");
  const { t: tBaustelle } = useT("baustelle");
  const { t: tKunde } = useT("customer");
  return (
    <Guard
      permission={"baustelle:view"}
      fallback={<ErrorAlert error={notPermitted()} />}
    >
      <Paper
        elevation={1}
        sx={{
          marginTop: 2,
          padding: 3,
          marginLeft: 2,
          marginRight: 2,
          position: "relative",
        }}
      >
        <Typography variant={"h5"}>Konto</Typography>
        <Typography sx={{ mt: 2, mb: 1 }} variant={"h6"}>
          {tKunde("construction-site_one") +
            " " +
            baustelle.nummer +
            " " +
            baustelle.name +
            (baustelle.bauleiterName
              ? ", " +
                tBaustelle("field.bauleiter") +
                ": " +
                baustelle.bauleiterName
              : "")}
        </Typography>
        <Formik
          validationSchema={schema}
          initialValues={{
            startDate: startDatum,
            endDate: new Date().toISOString().split("T")[0],
          }}
          onSubmit={() => {}}
        >
          {({ isSubmitting, isValidating, isValid, dirty, values }) => (
            <>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Field
                  component={TextField}
                  name="startDate"
                  label={t("start-date")}
                  type="date"
                  value={startDatum}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: values.endDate }}
                  onChange={(e: any) => setStartDatum(e.target.value)}
                />
                <Field
                  sx={{ ml: 2 }}
                  component={TextField}
                  name="endDate"
                  label={t("end-date")}
                  type="date"
                  value={endDatum}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: values.startDate }}
                  onChange={(e: any) => setEndDatum(e.target.value)}
                />
              </Stack>
            </>
          )}
        </Formik>
        <TableContainer
          component={Paper}
          sx={{
            width: {
              xs: "100%",
              md: "50%",
            },
            marginLeft: 0,
            mt: 2,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ fontSize: "1.1rem", border: "1px solid #ddd" }}
                >
                  {t("booking-short-type")}
                </TableCell>
                <TableCell
                  align={"right"}
                  sx={{ fontSize: "1.1rem", border: "1px solid #ddd" }}
                >
                  {t("costs")}
                </TableCell>
                <TableCell
                  align={"right"}
                  sx={{ fontSize: "1.1rem", border: "1px solid #ddd" }}
                >
                  {t("yields")}
                </TableCell>
                <TableCell
                  align={"right"}
                  sx={{ fontSize: "1.1rem", border: "1px solid #ddd" }}
                >
                  {t("balance")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data && data.items.length > 0 ? (
                data.items.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#f5f5f5" : "#ffffff",
                        border: "1px solid #ddd",
                      }}
                    >
                      {row.buchungsArt}
                    </TableCell>
                    <TableCell
                      align={"right"}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#f5f5f5" : "#ffffff",
                        border: "1px solid #ddd",
                      }}
                    >
                      {row.kosten != null && !isNaN(row.kosten)
                        ? row.kosten
                            ?.toFixed(2)
                            .replace(".", ",")
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " €"
                        : ""}
                    </TableCell>
                    <TableCell
                      align={"right"}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#f5f5f5" : "#ffffff",
                        border: "1px solid #ddd",
                      }}
                    >
                      {row.ertrag != null && !isNaN(row.ertrag)
                        ? row.ertrag
                            .toFixed(2)
                            .replace(".", ",")
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " €"
                        : ""}
                    </TableCell>
                    <TableCell
                      align={"right"}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#f5f5f5" : "#ffffff",
                        border: "1px solid #ddd",
                      }}
                    >
                      {row.saldo != null && !isNaN(row.saldo)
                        ? row.saldo
                            ?.toFixed(2)
                            .replace(".", ",")
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " €"
                        : ""}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <>
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {t("no-entries")}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
            <TableFooter>
              {data && data.items.length > 0 ? (
                <TableRow>
                  <TableCell sx={{ fontSize: "1rem" }}>
                    <Typography variant="subtitle1">
                      {t("sum") + ":"}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align={"right"}
                    sx={{ fontSize: "1rem", border: "1px solid #ddd" }}
                  >
                    {sumKosten != null && !isNaN(sumKosten)
                      ? sumKosten
                          .toFixed(2)
                          .replace(".", ",")
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " €"
                      : ""}
                  </TableCell>
                  <TableCell
                    align={"right"}
                    sx={{ fontSize: "1rem", border: "1px solid #ddd" }}
                  >
                    {sumErtrag != null && !isNaN(sumErtrag)
                      ? sumErtrag
                          .toFixed(2)
                          .replace(".", ",")
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " €"
                      : ""}
                  </TableCell>
                  <TableCell
                    align={"right"}
                    sx={{ fontSize: "1rem", border: "1px solid #ddd" }}
                  >
                    {sumSaldo != null && !isNaN(sumSaldo)
                      ? sumSaldo
                          .toFixed(2)
                          .replace(".", ",")
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " €"
                      : ""}
                  </TableCell>
                </TableRow>
              ) : (
                <></>
              )}
            </TableFooter>
          </Table>
        </TableContainer>
        <Divider sx={{ mt: 2, mb: 2 }} />
        <Grid container>
          <Grid item xs={12} md={3}>
            <BaustellenMaterialCard
              title={"Material von Baustelle -> Deponie/Abladestellen"}
              konto={kontoVon}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ pl: { xs: 0, md: 2 } }}>
            <BaustellenMaterialCard
              title={"Material an Baustelle"}
              konto={kontoAn}
            />
          </Grid>
          <Grid item xs={1} sx={{ pl: { xs: 0, md: 2 } }}>
            <Grid
              container
              direction="column"
              sx={{ height: "100%" }} // gleiche Höhe wie Nachbarkarten
            >
              <Grid item sx={{ flex: 1, display: "flex" }}>
                <Card sx={{ flex: 1 }}>
                  <Typography sx={{ ml: 2, mt: 2 }}>
                    {"Einsatzdauer Personal"}
                  </Typography>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <LabelListItem label={"Stunden Personal"}>
                          {formatGermanNumber(personalStunden, 2)}
                        </LabelListItem>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item sx={{ flex: 1, display: "flex", mt: 1 }}>
                <Card sx={{ flex: 1 }}>
                  <Typography sx={{ ml: 2, mt: 2 }}>
                    {"Einsatzdauer Fuhrpark"}
                  </Typography>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <LabelListItem label={"Stunden Fuhrpark"}>
                          {formatGermanNumber(fuhrparkStunden, 2)}
                        </LabelListItem>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Guard>
  );
}
function useUmsatzAuwertung(id: string) {
  const sucheUmsatzAuswertung =
    useSucheUmsatzAuswertungByBaustellenIdAndByDate();
  const queryClient = useQueryClient();

  return useMutation(
    (input: UmsatzAuswertungDaten) => {
      return sucheUmsatzAuswertung(id, input);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["baustellen", id]);
      },
    }
  );
}

function useKontoAuswertungByDate(id: string, richtung: string) {
  const sucheKontoAuswertung = useGetUmsatzMengenAuswertungByDate();
  const queryClient = useQueryClient();

  return useMutation(
    (input: UmsatzAuswertungRequest) => {
      return sucheKontoAuswertung(id, richtung, input);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["baustellen", id]);
      },
    }
  );
}
function useKontoStundenAuswertungByDate(id: string, buchungstyp: string) {
  const sucheKontoStunden = useGetUmsatzStundenAuswertungByDate();
  const queryClient = useQueryClient();

  return useMutation(
    (input: UmsatzAuswertungRequest) => {
      return sucheKontoStunden(id, buchungstyp, input);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["baustellen", id]);
      },
    }
  );
}
